import React, { useEffect, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';

interface DateTimeTextInputProps {
  id?: string;
  value?: Date | null;
  onChange: (value: Date | null) => void;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
}

const toDisplay = (val: Date | null | undefined): string => {
  if (!val) return '';
  const d = new Date(val);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
};

const DateTimeTextInput: React.FC<DateTimeTextInputProps> = ({ id, value, onChange, disabled, readOnly, required }) => {
  const [displayValue, setDisplayValue] = useState(() => toDisplay(value));
  const lastExternalValue = useRef(value);

  useEffect(() => {
    if (value !== lastExternalValue.current) {
      lastExternalValue.current = value;
      setDisplayValue(toDisplay(value));
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setDisplayValue(val);

    if (val === '') {
      lastExternalValue.current = null;
      onChange(null);
      return;
    }

    const match = val.match(/^(\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2})$/);
    if (match) {
      const [, dd, mm, yyyy, hh, min] = match;
      const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(min));
      if (
        date.getFullYear() === Number(yyyy) &&
        date.getMonth() === Number(mm) - 1 &&
        date.getDate() === Number(dd)
      ) {
        lastExternalValue.current = date;
        onChange(date);
      }
    }
  };

  return (
    <InputText
      id={id}
      value={displayValue}
      onChange={handleChange}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      placeholder="DD.MM.RRRR HH:MM"
    />
  );
};

export default DateTimeTextInput;
